import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import Text from "@/components/ui/text";
import { PushAction } from "@/entities/messaging/types";
import { useLeadDealSuggestion } from "@/pages/leads/hooks";
import { LeadOpenStatuses, LeadStatus } from "@/pages/leads/types";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { getEnumTKey } from "@/utils/strings";

interface Props {
  action: PushAction;
}

/**
 * "Convert" + "View lead" buttons on a Dreamer lead-to-deal suggestion. Convert
 * lands on the lead page with ?convert=1, which opens the convert modal only
 * while the lead is still open to convert, so Convert is enabled only while
 * the suggestion is still actionable. Once it isn't (the suggestion was
 * dismissed or cleared, or the lead is no longer open or was deleted), Convert
 * stays visible but disabled, next to the lead's current status. A dismissed
 * suggestion on a lead that is still open shows no status, since the dismissal
 * is the reason. The deep link is flag-gated, so Convert is hidden with the
 * flag off.
 */
export default function DreamerLeadConvertActions({ action }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enabled: isFlagEnabled } = useFeatureFlag(
    FeatureFlags.CRM_10630_LEAD_TO_DEAL_SUGGESTION
  );
  const leadId = action.leadId ?? "";
  // The message outlives the lead's status, so read the suggestion as it is now.
  const { data: suggestion } = useLeadDealSuggestion(leadId, isFlagEnabled);
  const canConvert = isFlagEnabled && !!suggestion?.isActionable;
  const leadStatus = suggestion?.leadStatus;
  const showLeadStatus =
    isFlagEnabled &&
    !!suggestion &&
    !suggestion.isActionable &&
    leadStatus != null &&
    leadStatus in LeadStatus &&
    !(
      suggestion.status === "dismissed" &&
      LeadOpenStatuses.includes(leadStatus as LeadStatus)
    );
  const leadPath = `/leads/${encodeURIComponent(leadId)}`;

  return (
    <div className="px-5 pt-2 flex items-center gap-3">
      {isFlagEnabled && (
        <Button
          variant="secondary"
          size="lg"
          disabled={!canConvert}
          onClick={() => navigate(`${leadPath}?convert=1`)}
          data-test="dreamer-lead-convert-button"
        >
          {t("chat.dreamerLeadConvertActions.convert")}
        </Button>
      )}
      <Button
        variant="secondary"
        size="lg"
        onClick={() => navigate(leadPath)}
        data-test="dreamer-lead-view-button"
      >
        {t("chat.dreamerLeadConvertActions.viewLead")}
      </Button>
      {showLeadStatus && (
        <Text
          as="span"
          variant="smBodySecondary"
          data-test="dreamer-lead-status"
        >
          {t("chat.dreamerLeadConvertActions.leadStatus", {
            status: t(
              getEnumTKey(
                "leads",
                "statusValues",
                LeadStatus,
                leadStatus as LeadStatus
              )
            )
          })}
        </Text>
      )}
    </div>
  );
}
